import api from './api';

/**
 * #3031 — per-capability delegation of integration setup.
 *
 * A farm owner can let one of their employees set up the email integration
 * without handing over credentials and without becoming an owner. The gate the
 * BFF enforces is a grant record, not the binary `account_type`.
 *
 * The one behaviour every caller has to understand: a grant takes effect at the
 * **grantee's next login**. The BFF mints its session once, at login, from the
 * backend's `/me`, and never refreshes permissions on a live session — so the UI
 * must say so rather than let the employee walk into a 403 it cannot explain.
 * `EMAIL_SETUP_CAPABILITY` plus `UserProfile.session_integration_capabilities`
 * (what the session will actually enforce) versus `integration_capabilities`
 * (the stored truth) is how the UI detects that state; see
 * `needsReloginForEmailSetup`.
 */

/** Must match `backend/src/core/capabilities.py` and `bff-service/src/core/capabilities.py`. */
export const EMAIL_SETUP_CAPABILITY = 'integrations:email:setup';

export interface IntegrationGrant {
    id: string;
    tenant_user_id: string;
    grantee_user_id: string;
    capability: string;
    granted_at?: string | null;
}

export interface GrantableEmployee {
    user_id: string;
    phone: string;
    first_name?: string | null;
    last_name?: string | null;
    granted_capabilities: string[];
}

export interface IntegrationGrantListResponse {
    grants: IntegrationGrant[];
    employees: GrantableEmployee[];
    delegable_capabilities: string[];
}

export interface CreateIntegrationGrantRequest {
    grantee_user_id: string;
    capability: string;
}

/**
 * True when the stored grant exists but this browser session predates it, so the
 * BFF will still refuse. The only fix is a re-login — by design.
 */
export function needsReloginForEmailSetup(profile: {
    integration_capabilities?: string[] | null;
    session_integration_capabilities?: string[] | null;
} | null | undefined): boolean {
    if (!profile) return false;
    const live = profile.integration_capabilities ?? [];
    // `undefined` means a BFF that predates #3031 (rolling deploy) — it cannot be
    // reported as a mismatch, or every page load would show the banner.
    const inSession = profile.session_integration_capabilities;
    if (inSession === undefined || inSession === null) return false;
    return live.includes(EMAIL_SETUP_CAPABILITY) && !inSession.includes(EMAIL_SETUP_CAPABILITY);
}

/** The seat permission the BFF's integration gate reads (HOF-627). */
export const SEAT_INTEGRATIONS_PERMISSION = 'integrations.manage';

/** True when this session may actually reach `/connect` and `/test`. */
export function sessionCanSetUpEmail(profile: {
    account_type?: string;
    session_integration_capabilities?: string[] | null;
    session_farm_permissions?: string[] | null;
} | null | undefined): boolean {
    if (!profile) return false;
    // HOF-627 (codex round 29): the BFF gate reads the session's seat snapshot; mirror it. Absent
    // (a session minted before the snapshot, or an older BFF): the legacy rule, as the gate keeps.
    if (Array.isArray(profile.session_farm_permissions)) {
        return profile.session_farm_permissions.includes(SEAT_INTEGRATIONS_PERMISSION);
    }
    if (profile.account_type !== 'employee') return true;
    return (profile.session_integration_capabilities ?? []).includes(EMAIL_SETUP_CAPABILITY);
}

class IntegrationGrantService {
    /** Active grants for the owner's farm plus the employees they may delegate to. */
    async getGrants(): Promise<IntegrationGrantListResponse> {
        const response = await api.get<IntegrationGrantListResponse>('/integration-grants', {
            withCredentials: true,
        });
        return response.data;
    }

    /** Grant one capability to one of the owner's own employees. */
    async createGrant(data: CreateIntegrationGrantRequest): Promise<IntegrationGrant> {
        const response = await api.post<IntegrationGrant>('/integration-grants', data, {
            withCredentials: true,
        });
        return response.data;
    }

    /**
     * Revoke a grant. Unlike granting, this takes effect immediately — the BFF
     * invalidates the grantee's sessions, so they are logged out.
     */
    async revokeGrant(grantId: string): Promise<void> {
        await api.delete(`/integration-grants/${grantId}`, { withCredentials: true });
    }
}

export const integrationGrantService = new IntegrationGrantService();
